import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, HStack, Text, useToast } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { getPaperworkItem, uploadPaperworkVersion } from "../../services/orderPaperwork";
import { getSignedFilesUrl } from "../../utils/signedUrls";
import { closeMessages } from "../../utils/messagingBus";
import { paperworkLoadError } from "../../utils/paperworkForms";

const STATUS_LABEL = { detected: "Blank", filled: "Filled — needs signature", signed: "Signed" };
// The server's links last an hour; one younger than this is used as it is.
const LINK_FRESH_MS = 50 * 60 * 1000;

// The form a paperwork task carries (Orders → Paperwork → Assign as task):
// open it (print from the viewer), upload a filled version, or open the
// patient's chart on it to fill it in the editor. sourceRef: "<visitId>:<formId>".
export default function PaperworkAttachment({ sourceRef, schema }) {
  const [visitId, formId] = String(sourceRef || "").split(":");
  const [form, setForm] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const input = useRef(null);
  const loadedAt = useRef(0);
  const toast = useToast();
  const navigate = useNavigate();

  const load = useCallback(async () => {
    if (!visitId || !formId) return;
    try {
      const res = await getPaperworkItem({ patientId: visitId, paperworkId: formId });
      loadedAt.current = Date.now();
      setForm(res?.form || null);
      setError(null);
    } catch (err) {
      setError(paperworkLoadError(err));
    }
  }, [visitId, formId]);
  useEffect(() => { load(); }, [load]);

  // The server's link (whoever the form was handed to may not be able to sign
  // one themselves): straight from the click while it is fresh — no round
  // trip a popup blocker could hold against it — else asked for again.
  const open = async () => {
    try {
      let url = form.url && Date.now() - loadedAt.current < LINK_FRESH_MS ? form.url : null;
      if (!url) {
        const fresh = await getPaperworkItem({ patientId: visitId, paperworkId: formId });
        url = fresh?.form?.url || await getSignedFilesUrl(form.path);
      }
      if (url) window.open(url, "_blank", "noopener,noreferrer");
      else toast({ title: `Could not open ${form.name}`, status: "error", duration: 4000 });
    } catch (err) {
      toast({ title: `Could not open ${form.name}`, description: err?.message, status: "error", duration: 4000 });
    }
  };

  const upload = async (file) => {
    setBusy(true);
    try {
      await uploadPaperworkVersion({ patientId: visitId, paperworkId: formId, file });
      await load();
      toast({ title: "Uploaded — mark the task done to send it back", status: "success", duration: 3000 });
    } catch (err) {
      toast({ title: "Couldn't upload it", description: err?.message, status: "error", duration: 5000 });
    } finally {
      setBusy(false);
    }
  };

  const openInChart = () => {
    const params = new URLSearchParams({ orders: "paperwork", paperwork: formId });
    if (schema) params.set("schema", schema);
    closeMessages();
    // /visit, not /clinic: roles.json closes /clinic/:uuid to admins and other
    // staff — who a form is usually handed to. Same chart page either way.
    navigate(`/visit/${visitId}?${params.toString()}`);
  };

  if (!visitId || !formId) return null;
  return (
    <Box mt={1.5} p={2} borderWidth="1px" borderRadius="md" bg="gray.50" _dark={{ bg: "gray.700" }}
         data-testid="paperwork-attachment">
      {error ? (
        <Text fontSize="xs" color="red.600">{error}</Text>
      ) : (
        <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
          📄 {form ? `${form.name} · ${STATUS_LABEL[form.status] || STATUS_LABEL.detected}` : "Loading the form…"}
        </Text>
      )}
      <HStack spacing={1} mt={1} flexWrap="wrap">
        <Button size="xs" variant="outline" onClick={open} isDisabled={!form}>Open</Button>
        <Button size="xs" variant="outline" onClick={() => input.current?.click()} isLoading={busy} isDisabled={!form}>
          Upload filled version
        </Button>
        <Button size="xs" variant="outline" colorScheme="blue" onClick={openInChart}>Open in chart</Button>
      </HStack>
      <input ref={input} type="file" hidden aria-label="Filled version file"
             accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
             onChange={(e) => {
               const file = e.target.files?.[0];
               e.target.value = "";
               if (file) upload(file);
             }} />
    </Box>
  );
}
